import type { HTMLAttributes } from 'react'

import { useHolo, type CardTilt } from './useHolo'
import styles from './Card.module.css'

export type CardSizeName = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero'

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Адрес лица. Пакет не знает, что на картинке — это забота приложения. */
  face?: string
  /** Адрес рубашки. Один на всю колоду. */
  back: string
  /** Подпись для скринридера: «Башня» или «Карта рубашкой вверх». */
  label: string

  /** Показано лицо. Управляется снаружи — карта не решает сама, когда открыться. */
  revealed?: boolean
  /**
   * Карта легла перевёрнутой.
   *
   * Ориентация обязана читаться без вглядывания, поэтому кроме поворота
   * рисунка выводится значок. Один поворот на 180° человек не заметит,
   * если не знает наизусть, где у карты верх.
   */
  reversed?: boolean

  size?: CardSizeName
  /** Выбрана в веере: приподнята, с золотой рамкой и свечением. */
  selected?: boolean
  /** Приглушена: выбирают не её. */
  dimmed?: boolean
  /** Ожидание картинки. Шиммер включать только если ждём дольше ~260мс. */
  loading?: boolean

  /**
   * Фольга: перелив по наклону и по пальцу.
   *
   * Выключена по умолчанию и должна такой оставаться везде, кроме карты дня.
   * Голография работает ровно до тех пор, пока она редкость: тот же перелив
   * на всех 78 картах в сетке превращает дорогой приём в наклейку.
   */
  holo?: boolean
  /**
   * Наклон устройства, −1…1 по каждой оси. Приходит снаружи: пакет карт
   * не знает про Telegram и не должен. Без него фольга работает от пальца.
   */
  tilt?: CardTilt | null

  onSelect?: () => void
}

/**
 * Карта.
 *
 * Структура трёхслойная и продиктована 3D-переворотом: внешний элемент держит
 * перспективу, внутренний вращается, лицо и рубашка — его стороны.
 *
 * Рамка и свечение вынесены отдельными слоями специально: их анимация не должна
 * вызывать перерисовку самой картинки.
 */
export function Card({
  face,
  back,
  label,
  revealed = false,
  reversed = false,
  size = 'lg',
  selected = false,
  dimmed = false,
  loading = false,
  holo = false,
  tilt,
  onSelect,
  className,
  style,
  ...rest
}: CardProps) {
  const interactive = Boolean(onSelect)
  const { ref: holoRef, holoProps } = useHolo(holo, tilt)

  const classes = [
    styles.card,
    revealed ? styles.revealed : null,
    selected ? styles.selected : null,
    dimmed ? styles.dimmed : null,
    loading ? styles.loading : null,
    interactive ? styles.interactive : null,
    holo ? styles.holoHost : null,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      <div className={styles.inner}>
        <img className={styles.back} src={back} alt="" draggable={false} />
        {/* Лицо держим в разметке всегда: подгружать его в момент переворота
            значит показать пустую карту на середине анимации. */}
        <img
          className={[styles.face, reversed ? styles.upsideDown : null].filter(Boolean).join(' ')}
          src={face}
          alt=""
          draggable={false}
          hidden={!face}
        />
      </div>

      {/* Порядок слоёв снизу вверх: картинка → лак → фольга → блик →
          рамка → свечение. Лак под фольгой намеренно: виньетка принадлежит
          изображению, а фольга лежит НА нём, как плёнка на печати. */}
      <span className={styles.lacquer} aria-hidden="true" />

      {holo ? (
        <>
          <span className={styles.holo} aria-hidden="true" />
          <span className={styles.glare} aria-hidden="true" />
        </>
      ) : null}

      {revealed ? <span className={styles.sweep} aria-hidden="true" /> : null}

      <span className={styles.frame} aria-hidden="true" />
      <span className={styles.glow} aria-hidden="true" />

      {reversed && revealed ? (
        <span className={styles.reversedMark} title="Перевёрнутая">
          ↻
        </span>
      ) : null}
    </>
  )

  const styleVars = { '--card-w': `var(--card-w-${size})`, ...style } as React.CSSProperties

  if (interactive) {
    return (
      <button
        ref={holoRef}
        type="button"
        className={classes}
        style={styleVars}
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={label}
        {...holoProps}
        {...(rest as HTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    )
  }

  return (
    <div
      ref={holoRef}
      className={classes}
      style={styleVars}
      role="img"
      aria-label={label}
      {...holoProps}
      {...rest}
    >
      {content}
    </div>
  )
}
